// ===== ENHANCED NAVIGATION WITH RBAC =====
// src/Layout/AuthorisedUser/NavigationBar.jsx
import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import PermissionGate from "components/PermissionGate";
import { DASHBOARD_URL } from "constants/app";
import { useSession } from "hooks/useSession";
import { APP_CONFIG } from "../../constants/app"
import { fetchWrapper } from "utils/apiFetch";

// import "./Authorisation.css"
function NavigationBar() {

    const { user } = useAuth();
    const location = useLocation();
    const [expandedMenus, setExpandedMenus] = useState({});
    const { role } = useSession();

    // ── Robust role check (case/whitespace tolerant) ─────────────────
    const isPlatformUser = (role || "").trim().toLowerCase() === "platform user";
    const isAPIPartner   = (role || "").trim().toLowerCase() === "api partner";

    // ── Platform User module flags ─────────────────────────────────
    const [puModules, setPuModules] = useState({});

    useEffect(() => {
        if (!isPlatformUser) return;
        const fd = new FormData();
        fd.append("action", "GET_MY_MODULES");
        fetchWrapper("https://pro.mokshiq.in/platform_user_action", {
            method: "POST",
            body: fd,
        })
            .then((res) => { if (res?.status) setPuModules(res.data || {}); })
            .catch(() => {});
    }, [isPlatformUser]);

    const [extraServices, setExtraServices] = useState([]);

    useEffect(() => {
        if (isPlatformUser ) return;
        const fd = new FormData();
        fd.append("action", "GET");
        fd.append("company", "1839");   // ✅ fetch on behalf of logged-in user
        fetchWrapper("https://pro.mokshiq.in/extra_service_action", {
            method: "POST",
            body: fd,
        })
            .then((res) => {
                if (res?.status !== false) {
                    setExtraServices(res?.data || []);
                }
            })
            .catch(() => {});
    }, [isPlatformUser, "1839"]);
    // ──────────────────────────────────────────────────────────────

    console.log('logo', { logo: APP_CONFIG.ORGANISATION_LOGO, data: process.env })

    const toggleMenu = (menuKey) => {
        setExpandedMenus((prev) => ({
            ...prev,
            [menuKey]: !prev[menuKey],
        }));
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const isMenuActive = (paths) => {
        return paths.some((path) => location.pathname.startsWith(path));
    };

    return (
        <nav>
            <div className="app-logo" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <a className="logo d-inline-block" href={DASHBOARD_URL[role] || "/"} style={{ display: "inline-flex", alignItems: "center" }}>
                    <img
                        alt={APP_CONFIG.ORGANISATION_BRAND || "Logo"}
                        src={APP_CONFIG.ORGANISATION_LOGO || "/assets/images/logo/kryntiq.png"}
                        style={{ maxHeight: "38px", maxWidth: "100%", objectFit: "contain", filter: "contrast(1.06) saturate(1.1)" }}
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/assets/images/logo/kryntiq.png";
                        }}
                    />
                </a>

                <span class="bg-light-primary toggle-semi-nav">
                    <i class="ti ti-chevrons-right f-s-20"></i>
                </span>
            </div>
            <div
                className="app-nav"
                id="app-simple-bar"
                style={{
                    height: "calc(100vh - 70px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    background: "#fff",
                    paddingBottom: "80px",
                }}
            >
                <ul class="main-nav p-0 mt-2">
                    <li class="menu-title">
                        <span>Dashboard</span>
                    </li>
                    <li>
                        <a aria-expanded="false" class="" href={DASHBOARD_URL[role] || "/"}>
                            <i class="iconoir-home-alt"></i>
                            dashboard
                        </a>
                    </li>

                    {/* ══════════════════════════════════════════════════
                        PLATFORM USER — only shows their assigned modules
                        Everything below is hidden for Platform User
                    ══════════════════════════════════════════════════ */}
                    {isPlatformUser && (
                        <>
                            <li class="menu-title"><span>Platform Access</span></li>

                            {puModules.can_report && (
                                <li>
                                    <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#pu-reports">
                                        <i class="iconoir-lifebelt"></i>
                                        Reports
                                    </a>
                                    <ul class="collapse" id="pu-reports">
                                        <li><a href="/main_wallet_reports">Main Wallet Report</a></li>
                                        <li><a href="/recharge_reports">Recharge Report</a></li>
                                        <li><a href="/bbps_report">BBPS Report</a></li>
                                        <li><a href="/credit_debit_report">Credit Debit Report</a></li>
                                        <li><a href="/wallet_settlement_reports">Wallet Settlement Report</a></li>
                                        <li><a href="/pancard_reports">Pancard Report</a></li>
                                        <li><a href="/day_wise_report">Day Wise Report</a></li>
                                    </ul>
                                </li>
                            )}

                            {/* ── Member Manager — includes Credit/Debit + Member view ── */}
                            {(puModules.can_member_view || puModules.can_credit || puModules.can_debit) && (
                                <li>
                                    <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#pu-members">
                                        <i class="iconoir-community"></i>
                                        Member Manager
                                    </a>
                                    <ul class="collapse" id="pu-members">
                                        {puModules.can_member_view && (
                                            <li><a href="/member_management">Member Management</a></li>
                                        )}
                                        {puModules.can_credit && <li><a href="/member_management">Add Credit</a></li>}
                                        {puModules.can_debit  && <li><a href="/member_management">Add Debit</a></li>}
                                        {(puModules.can_credit || puModules.can_debit) && (
                                            <li><a href="/credit_debit_report">Credit Debit Report</a></li>
                                        )}
                                    </ul>
                                </li>
                            )}

                            {/* ── Add Fund / Approve Request — wallet load view+approve ── */}
                            {(puModules.can_credit || puModules.can_debit) && (
                                <li class="no-sub">
                                    <a class="" href="/load_wallet">
                                        <i class="iconoir-wallet"></i> Add Fund / Approve Request
                                    </a>
                                </li>
                            )}

                            <li class="menu-title"><span>Account</span></li>
                            <li>
                                <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#pu-profile">
                                    <i class="iconoir-handbag"></i>
                                    Profile Manager
                                </a>
                                <ul class="collapse" id="pu-profile">
                                    <li><a href="/member_setting_management">View Profile</a></li>
                                </ul>
                            </li>

                            <li class="menu-title"><span>Others</span></li>
                            <li class="no-sub">
                                <a class="" href="/ticket_management">
                                    <i class="iconoir-chat-bubble-question"></i> Support Ticket
                                </a>
                            </li>
                        </>
                    )}

                    {/* ══════════════════════════════════════════════════
                        ALL OTHER ROLES — unchanged from original
                    ══════════════════════════════════════════════════ */}
                    {!isPlatformUser && (
                        <>
                            <li class="menu-title"><span>Settings</span></li>
                            {(role === "Admin" || role === "Main Admin" ) && (
                                <>
                                    <li>
                                        <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#role-manager">

                                            <i class="iconoir-handbag"></i>
                                            Role Manager
                                        </a>
                                        <ul class="collapse" id="role-manager">
                                            <li><a href="/role_management">Role Manager</a></li>
                                        </ul>
                                    </li>

                                    
                                    <li class="no-sub">
                                        <a class="" href="/create_api_integration_request">
                                            <i class="iconoir-page-star"></i> Create API Integration Request
                                        </a>
                                    </li>
                                </>
                            )}
                             {(role === "Admin" || role === "Main Admin" || role === "API Partner") && (
                                 <li>
                                     <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#api-documentation-menu">
                                         <i class="iconoir-code"></i> API Documentation
                                     </a>
                                     <ul class="collapse" id="api-documentation-menu">
                                         <li>
                                             <a href="/api_docs">
                                                 <i class="iconoir-page-star me-2"></i> Recharge & BBPS API Docs
                                             </a>
                                         </li>
                                         <li>
                                             <a href="/verification_api_docs">
                                                 <i class="iconoir-user-scan me-2"></i> Verification & KYC API Docs
                                             </a>
                                         </li>
                                     </ul>
                                 </li>
                             )}
                            {(role === "Admin" || role === "Main Admin" || role === "White label") && (
                                <>
                                 <li>
                                        <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#company-manager">

                                            <i class="iconoir-handbag"></i>
                                            Company Manager
                                        </a>
                                        <ul class="collapse" id="company-manager">
                                            <li><a href="/company_management">Company Manager</a></li>
                                            <li><a href="/banner_management">Banner Manager</a></li>
                                            <li><a href="/news_section">News Manager</a></li>
                                            <li><a href="/extra_service_management">Extra Service Management </a></li>
                                            <li><a href="/agreement_list">Agreement Management </a></li>
                                            <li><a href="/admin_monthly_report">Admin Monthly Report </a></li>
                                        </ul>
                                    </li>
                                    <li>
                                        <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#resource-manager">
                                            <i class="iconoir-handbag"></i>
                                            Resource Manager
                                        </a>
                                        <ul class="collapse" id="resource-manager">
                                            <li><a href="/scheme_management">Scheme Manager</a></li>

                                            {(role === "Admin" || role === "Main Admin") && (
                                                <li><a href="/provider_management">Provider Manager</a></li>
                                            )}

                                            <li><a href="/charge_slab_management">Charge Slab Manager</a></li>
                                            <li><a href="/commission_type_management">Commission Type Manager</a></li>
                                            <li><a href="/commission_management">Commission Manager</a></li>

                                            {(role === "Admin" || role === "Main Admin") && (
                                                <li><a href="/api_management">API Manager</a></li>
                                                
                                            )}
                                        </ul>
                                    </li>

                                    {(role === "Admin" || role === "Main Admin") && (
                                        <li>
                                            <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#service-manager">
                                                <i class="iconoir-handbag"></i>
                                                Service Manager
                                            </a>
                                            <ul class="collapse" id="service-manager">
                                                <li><a href="/plan_manager">Plan Manager</a></li>
                                                <li><a href="/plan_service_charge">Plan Service Charge</a></li>
                                                <li><a href="/retailer_plan_page">Retailer Plan Assign</a></li>
                                                <li><a href="/main_provider_restriction">Provider Based Restriction</a></li>
                                                <li><a href="/user_based_provider_restriction">User Based Restriction</a></li>
                                                <li><a href="/sms_charge">SMS charge</a></li>
                                                <li><a href="/purchase_services">Purchase Services</a></li>
                                                <li><a href="/extra_services">Extra Services Manager</a></li>
                                            </ul>
                                        </li>
                                        
                                    )}

                                    {(role === "Admin" || role === "Main Admin") && (
                                        <>
                                            <li>
                                                <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#website-manager">
                                                    <i class="iconoir-handbag"></i>
                                                    Website Plan Manager
                                                </a>
                                                <ul class="collapse" id="website-manager">
                                                    <li><a href="/plan_info">Plan Info</a></li>
                                                    <li><a href="/payment">Payment Details</a></li>
                                                    <li><a href="/payment_history">Payment History</a></li>
                                                    <li><a href="/other_charge_list">Other Charge History</a></li>
                                                    <li><a href="/other_payment_history">Other Payment History</a></li>
                                                    <li><a href="/paid_services">Paid Service Management</a></li>
                                                </ul>
                                            </li>
                                            {/* Deposit & Whitelist Approval — Admin level only */}
                                            <li>
                                                <a aria-expanded="false" data-bs-toggle="collapse" href="#fund-approval-manager">
                                                    <i className="iconoir-wallet"></i>
                                                    Deposit & Whitelist Approval
                                                </a>
                                                <ul className="collapse" id="fund-approval-manager">
                                                    <li><a href="/bank_accounts">Load Request Approval</a></li>
                                                    <li><a href="/bank_whitelist">Bank Whitelist Approval</a></li>
                                                    <li><a href="/load_wallet">Add Fund / Top-up</a></li>
                                                </ul>
                                            </li>
                                        </>
                                        
                                    )}

                                    {/* Platform User Management — Admin level only */}
                                    <li class="no-sub">
                                        <a class="" href="/platform_user_management">
                                            <i class="iconoir-user-badge-check"></i> Platform Users
                                        </a>
                                    </li>
                                </>
                            )}
                            {(role === "Admin" || role === "Main Admin" || role === "White label" || role === "Distributer" || role === "Master Distributor") && (
                                <>
                                    <li>
                                        <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#member-manager">
                                            <i class="iconoir-handbag"></i>
                                            Member Manager
                                        </a>
                                        <ul class="collapse" id="member-manager">
                                            <li><a href="/member_management">Member Management</a></li>

                                            {(role === "Admin" || role === "White label") && (
                                                <>
                                                    <li><a href="/deleted_member_management">Deleted Member</a></li>
                                                    <li><a href="/security_block_management">Blocked Member</a></li>
                                                </>
                                            )}
                                        </ul>
                                    </li>
                                </>
                            )}
                            <li>
                                <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#profile-manager">

                                    <i class="iconoir-handbag"></i>
                                    Profile Manager
                                </a>
                                <ul class="collapse" id="profile-manager">
                                    <li><a href="/member_setting_management">View Profile</a></li>
                                    <li><a href="/user_commission">Recharge & BBPS Commission</a></li>
                                    <li><a href="/user_other_commission">Other Service Commission</a></li>
                                </ul>
                            </li>


                            <li class="menu-title"><span>Reports & Analytics </span></li>

                            <li>
                                <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#chart">
                                    <i class="iconoir-lifebelt"></i> Reports
                                </a>
                                <ul class="collapse" id="chart">
                                    <li><a href="/reports">Financial & Txn Dashboard</a></li>
                                    <li><a href="/main_wallet_reports">Main Wallet Report</a></li>
                                    <li class="another-level">
                                        <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#apexcharts-page">
                                            Product Wise Report
                                        </a>
                                        <ul class="collapse" id="apexcharts-page">
                                            {role === "Pancard Agent" ? (
                                                <li><a href="/pancard_reports">Pancard Report</a></li>
                                            ) : (
                                                <>
                                                    <li><a href="/aeps_reports">AEPS Report</a></li>
                                                    <li><a href="/dmt_reports">DMT Report</a></li>
                                                    <li><a href="/recharge_reports">Recharge Report</a></li>
                                                    <li><a href="/bbps_report">BBPS Report</a></li>
                                                    <li><a href="/offline_bbps_report">Offline BBPS Report</a></li>
                                                    <li><a href="/pancard_reports">Pancard Report</a></li>
                                                    {!isAPIPartner && (
                                                        <li><a href="/upi_qr_reports">UPI QR Report</a></li>
                                                    )}
                                                    {!isAPIPartner && (
                                                        <li><a href="/wallet_settlement_reports">Wallet Settlement Report</a></li>
                                                    )}
                                                    {/* <li><a href="/pancard_reports">Pancard Report</a></li>
                                                    <li><a href="/intrawallet_reports">Intra Wallet Report</a></li> */}
                                                    <li><a href="/credit_debit_report">Credit Debit Report</a></li>
                                                    <li><a href="/day_wise_report">Day Wise Report</a></li>
                                                    <li><a href="/user_wise_report">User and Operator Wise Report</a></li>
                                                    {!isAPIPartner && (
                                                        <li><a href="/fund_summary_report">FUND Transfer Report</a></li>
                                                        
                                                    )}
                                                </>
                                            )}
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                            {(role === "Retailer") && (<>
                                <li class="menu-title"><span>Our Services </span></li>

                                <li>
                                    <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#advance-ui">
                                        <i class="iconoir-shopping-bag-plus"></i> Recharge Service
                                        <span class="badge text-warning-dark bg-warning-400 badge-notification ms-2">
                                            2+
                                            <span class="visually-hidden"></span>
                                        </span>
                                    </a>
                                    <ul class="collapse" id="advance-ui">
                                        <li><a href="/mobile_recharge">Mobile Recharge</a></li>
                                        <li><a href="/dth_recharge">DTH Recharge</a></li>
                                    </ul>
                                </li>
                                <li>
                                    <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#advance-bb">
                                        <i class="iconoir-shopping-bag-plus"></i> BBPS Services
                                        <span class="badge text-warning-dark bg-warning-400 badge-notification ms-2">
                                            1+
                                            <span class="visually-hidden"></span>
                                        </span>
                                    </a>
                                    <ul class="collapse" id="advance-bb">
                                        <li><a href="/bill_payment">BBPS 1</a></li>
                                    </ul>
                                </li>
                            </>)}


                            <li class="menu-title"><span>Others</span></li>
                                {extraServices.filter((s) => s.Status && (role === "Admin" || role === "Main Admin" || !s.Name?.toLowerCase().includes("whatsapp"))).length > 0 && (
                                 <li>
                                     <a aria-expanded="false" class="" data-bs-toggle="collapse" href="#maps">
                                         <i class="iconoir-map"></i> Extra Services
                                     </a>
                                     <ul class="collapse" id="maps">
                                         {extraServices
                                             .filter((s) => s.Status && (role === "Admin" || role === "Main Admin" || !s.Name?.toLowerCase().includes("whatsapp")))
                                             .map((service) => (
                                                 <li key={service.id}>
                                                     
                                                      <a   href={service["Web URL"] || "#"}
                                                         target="_blank"
                                                         rel="noopener noreferrer">
                                                     
                                                         {service.Name}
                                                     </a>
                                                 </li>
                                             ))}
                                     </ul>
                                 </li>
                             )}
                            <li class="no-sub">
                                <a class="" href="/load_wallet">
                                    <i class="iconoir-wallet"></i> Add Fund / Approve Request
                                </a>
                            </li>
                            

                            <li class="no-sub">
                                <a class="" href="/ticket_management">
                                    <i class="iconoir-chat-bubble-question"></i> Support Ticket
                                </a>
                            </li>
                        </>
                    )}

                </ul>
            </div>

            <div class="menu-navs">
                <span class="menu-previous"><i class="ti ti-chevron-left"></i></span>
                <span class="menu-next"><i class="ti ti-chevron-right"></i></span>
            </div>

        </nav>
    );
}

export default NavigationBar;